import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { testimonials } from '../mock';
import { Star, Quote } from 'lucide-react';

export default function Testimonials() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  // Duplicate for seamless marquee
  const row = [...testimonials, ...testimonials];

  return (
    <section id="testimonials" className="py-28 md:py-40 bg-[#0D1612] relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-emerald-500/[0.06] rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 mb-16" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
            / Client Love
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95]">
            Don&rsquo;t take our<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">word for it.</span>
          </h2>
        </motion.div>
      </div>

      {/* Marquee row */}
      <div className="relative flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent)]">
        <div className="flex animate-marquee-fast gap-6">
          {row.map((t, i) => <TestimonialCard key={i} t={t} />)}
        </div>
        <div className="flex animate-marquee-fast gap-6 ml-6" aria-hidden="true">
          {row.map((t, i) => <TestimonialCard key={`b-${i}`} t={t} />)}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="w-[400px] flex-shrink-0 bg-[#050A08] rounded-3xl p-8 border border-white/[0.06] hover:border-emerald-500/30 transition-colors duration-500">
      <Quote className="w-9 h-9 text-emerald-500/30 mb-5" />
      <div className="flex gap-1 mb-5">
        {[...Array(t.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-emerald-400 text-emerald-400" />
        ))}
      </div>
      <p className="text-slate-300 leading-relaxed mb-8 whitespace-normal">&ldquo;{t.content}&rdquo;</p>
      <div className="flex items-center gap-4">
        <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500/40" />
        <div>
          <div className="font-semibold text-white">{t.name}</div>
          <div className="text-sm text-slate-500">{t.role} &middot; {t.company}</div>
        </div>
      </div>
    </div>
  );
}
